import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { buildAnalyticsEvent } from '@utils/buildAnalyticsEvent';
import classNames from 'classnames';
import PropTypes, { string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { useLiteStorefront } from '@hooks/use-lite-storefront';
import AnalyticsConstants from '../../../../../../../constants/analytics';
import TrackableLink from '../../../../../../components/trackable_link';
import Phone from './Phone';
import Styles from './styles.scss';

const clickThroughTrackingData = (analyticsProperties, props) => {
	const { memberId, vendor } = props;
	return buildAnalyticsEvent({
		analyticsEventType: AnalyticsConstants.CLICK_THROUGH_TO_WEBSITE,
		analyticsProperties: { ...analyticsProperties, memberId },
		vendor,
	});
};

const constructInstagramLink = (instagramUsername) => {
	return instagramUsername.length
		? `http://www.instagram.com/${instagramUsername}`
		: '';
};

const constructPinterestLink = (pinterestUsername) => {
	return pinterestUsername.length
		? `http://www.pinterest.com/${pinterestUsername}`
		: '';
};

const constructTwitterLink = (twitterUsername) => {
	return twitterUsername.length ? `https://x.com/${twitterUsername}` : '';
};

const PhoneForNonFooterContext = ({ vendor, track, isFooter, goTo }) => {
	if (!vendor.phone || isFooter) {
		return null;
	}

	return (
		<div className={Styles.phone}>
			<Phone track={track} vendor={vendor} goTo={goTo} />
		</div>
	);
};

const getLinkProps = (showAsText, showAsIcon) => {
	if (!showAsText) return { icon: 'internet' };
	if (!showAsIcon) return { text: 'Website' };
	return { icon: 'internet', text: 'Website' };
};

const websiteLink = (props) => {
	const { vendor, showAsIcon, showAsText } = props;
	const linkProps = getLinkProps(showAsText, showAsIcon);

	return (
		<TrackableLink
			websiteUrl={vendor.websiteUrl}
			{...linkProps}
			title="Website"
			trackingData={clickThroughTrackingData({ destination: 'website' }, props)}
			resetLinkStyle={true}
		/>
	);
};

const footerWebsiteLink = (props) => {
	const { vendor } = props;
	return (
		vendor.websiteUrl && (
			<div className={Styles.website}>{websiteLink(props)}</div>
		)
	);
};

const Social = (props) => {
	const {
		isFooter,
		social,
		vendor,
		containerClassname,
		iconSize = 'md',
	} = props;
	const { track } = useAnalyticsContext();
	const isLiteStorefront = useLiteStorefront();
	return (
		<div
			className={classNames(containerClassname, {
				[Styles.footer]: isFooter,
			})}
		>
			{isFooter &&
				footerWebsiteLink({
					...props,
					showAsIcon: true,
					showAsText: true,
				})}
			<div className={Styles.socialLinks}>
				<TrackableLink
					websiteUrl={social.facebookUrl}
					icon="facebook-circle"
					iconSize={iconSize}
					title="facebook"
					trackingData={clickThroughTrackingData(
						{ destination: 'facebook' },
						props,
					)}
					resetLinkStyle={true}
				/>
				<TrackableLink
					websiteUrl={constructTwitterLink(social.twitterUsername)}
					icon="x-circle"
					iconSize={iconSize}
					title="x"
					trackingData={clickThroughTrackingData(
						{ destination: 'twitter' },
						props,
					)}
					resetLinkStyle={true}
				/>
				<TrackableLink
					websiteUrl={constructInstagramLink(social.instagramUsername)}
					icon="instagram-circle"
					iconSize={iconSize}
					title="instagram"
					trackingData={clickThroughTrackingData(
						{ destination: 'instagram' },
						props,
					)}
					resetLinkStyle={true}
				/>
				<TrackableLink
					websiteUrl={constructPinterestLink(social.pinterestUsername)}
					icon="pinterest-circle"
					iconSize={iconSize}
					title="pinterest"
					trackingData={clickThroughTrackingData(
						{ destination: 'pinterest' },
						props,
					)}
					resetLinkStyle={true}
				/>
				<>
					{!isFooter &&
						websiteLink({
							...props,
							showAsIcon: true,
							showAsText: false,
						})}
					<PhoneForNonFooterContext
						vendor={vendor}
						track={track}
						isFooter={isFooter}
						{...(!isLiteStorefront && { goTo: 'contact' })}
					/>
				</>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	memberId: state.membership.member ? state.membership.member.id : '',
});

Social.propTypes = {
	isFooter: PropTypes.bool,
	social: PropTypes.shape({
		twitterUsername: PropTypes.string.isRequired,
		facebookUrl: PropTypes.string.isRequired,
		pinterestUsername: PropTypes.string.isRequired,
		instagramUsername: PropTypes.string.isRequired,
	}).isRequired,
	vendor: PropTypes.shape({
		extension: string,
		phone: string,
		websiteUrl: string,
	}).isRequired,
	memberId: PropTypes.string.isRequired,
	iconSize: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
	containerClassname: PropTypes.string,
};

Social.defaultProps = {
	isFooter: false,
	containerClassname: '',
};

export default connect(mapStateToProps)(Social);
